import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, eur, num } from "@/lib/api";
import { useI18n } from "@/i18n";
import { PageHeader, KpiCard } from "@/components/common";
import { ShoppingCart, AlertTriangle, Warehouse, TrendingUp, Boxes, Package } from "lucide-react";
import ExpiryWidget from "@/components/dashboard/ExpiryWidget";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function Dashboard() {
  const { t } = useI18n();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <div className="text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div>
      <PageHeader title={t("dash.title")} subtitle={t("dash.subtitle")} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiCard testId="kpi-open-orders" index={0} label={t("dash.openOrders")} value={num(d.open_orders)} icon={ShoppingCart} sub={t("dash.openOrdersSub")} />
        <KpiCard testId="kpi-understock" index={1} label={t("dash.understock")} value={num(d.understock_count)} icon={AlertTriangle} tone={d.understock_count > 0 ? "danger" : "default"} sub={t("dash.understockSub")} />
        {d.finance_visible && <>
          <KpiCard testId="kpi-stock-value" index={2} label={t("common.stockValue")} value={eur(d.stock_value)} icon={Warehouse} sub={t("dash.stockValueSub")} />
          <KpiCard testId="kpi-revenue" index={3} label={t("common.revenueNet")} value={eur(d.revenue)} icon={TrendingUp} tone="primary" sub={t("dash.revenueSub")} />
          <KpiCard testId="kpi-cogs" index={4} label={t("common.cogs")} value={eur(d.cogs)} icon={Package} sub={t("dash.cogsSub")} />
          <KpiCard testId="kpi-cm" index={5} label={t("common.margin")} value={eur(d.contribution_margin)} icon={TrendingUp} tone="primary" sub={t("dash.marginSub")} />
        </>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {d.finance_visible && <div className="lg:col-span-2 rounded-md border border-border bg-card p-5 shadow-sm">
          <h3 className="font-heading text-lg font-medium mb-4">{t("dash.revenueTrend")}</h3>
          {d.revenue_trend?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={d.revenue_trend}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip formatter={(v) => eur(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" fill="url(#rev)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
              {t("dash.noRevenue")}
            </div>
          )}
        </div>}
        <div className="space-y-4">
        <ExpiryWidget />
        <div className="rounded-md border border-border bg-card p-5 shadow-sm">
          <h3 className="font-heading text-lg font-medium mb-4">{t("dash.catalog")}</h3>
          <div className="space-y-3">
            <Link to="/produkte" className="flex items-center justify-between rounded-md border border-border p-3 hover:bg-muted transition-colors" data-testid="link-products">
              <span className="flex items-center gap-2 text-sm font-medium"><Package className="h-4 w-4" /> {t("prod.title")}</span>
              <span className="font-mono font-semibold">{d.total_products}</span>
            </Link>
            <Link to="/bundles" className="flex items-center justify-between rounded-md border border-border p-3 hover:bg-muted transition-colors" data-testid="link-bundles">
              <span className="flex items-center gap-2 text-sm font-medium"><Boxes className="h-4 w-4" /> {t("bundle.title")}</span>
              <span className="font-mono font-semibold">{d.total_bundles}</span>
            </Link>
            <Link to="/auftraege" className="flex items-center justify-between rounded-md border border-border p-3 hover:bg-muted transition-colors" data-testid="link-orders">
              <span className="flex items-center gap-2 text-sm font-medium"><ShoppingCart className="h-4 w-4" /> {t("dash.viewOrders")}</span>
              <span className="text-muted-foreground text-sm">→</span>
            </Link>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
